import React from 'react';
import { DimensionsIcon, StackIcon } from '@radix-ui/react-icons';
import type { TFunction } from 'i18next';
import type { PropertyResponseType } from '@services';

export type StatDescriptor = {
  key: string;
  icon: React.ReactNode;
  resolve: (p: PropertyResponseType) => unknown;
  format: (v: unknown, t: TFunction) => string;
};

const dimensionIcon = React.createElement(DimensionsIcon, { width: 16, height: 16 });
const stackIcon = React.createElement(StackIcon, { width: 16, height: 16 });

/**
 * Formats a raw dimension value with the meters suffix.
 */
const asMeters = (v: unknown): string => `${v} m²`;
/**
 * Coerces a raw stat value to its display string.
 */
const asText = (v: unknown): string => String(v);

/**
 * Localizes a form option value when a matching i18n key exists.
 */
function optionLabel(group: string, value: unknown, t: TFunction): string {
  const raw = String(value);
  const key = `form.options.${group}.${raw}`;
  const label = t(key, { defaultValue: '' });
  return label && label !== key ? label : raw;
}

export const STAT_DESCRIPTORS: StatDescriptor[] = [
  { key: 'area', icon: dimensionIcon, resolve: (p) => p.characteristics?.total_area, format: (v) => asMeters(v) },
  { key: 'constructedArea', icon: dimensionIcon, resolve: (p) => p.characteristics?.constructed_area, format: (v) => asMeters(v) },
  { key: 'privateArea', icon: dimensionIcon, resolve: (p) => p.characteristics?.private_area, format: (v) => asMeters(v) },
  { key: 'bedrooms', icon: stackIcon, resolve: (p) => p.characteristics?.bedrooms, format: asText },
  { key: 'suites', icon: stackIcon, resolve: (p) => p.characteristics?.suites, format: asText },
  { key: 'bathrooms', icon: stackIcon, resolve: (p) => p.characteristics?.bathrooms, format: asText },
  { key: 'totalRooms', icon: stackIcon, resolve: (p) => p.characteristics?.total_rooms, format: asText },
  { key: 'livingRooms', icon: stackIcon, resolve: (p) => p.characteristics?.living_rooms, format: asText },
  { key: 'parking', icon: stackIcon, resolve: (p) => p.characteristics?.parking_spots, format: asText },
  { key: 'floor', icon: stackIcon, resolve: (p) => p.characteristics?.floor_number, format: asText },
  { key: 'totalFloors', icon: stackIcon, resolve: (p) => p.characteristics?.total_floors, format: asText },
  { key: 'ceilingHeight', icon: dimensionIcon, resolve: (p) => p.characteristics?.ceiling_height, format: (v) => `${v} m` },
  { key: 'yearBuilt', icon: stackIcon, resolve: (p) => p.characteristics?.year_built, format: asText },
  {
    key: 'furnished',
    icon: stackIcon,
    resolve: (p) => p.characteristics?.furnished,
    format: (v, t) => optionLabel('furnished', v, t),
  },
  {
    key: 'facing',
    icon: stackIcon,
    resolve: (p) => p.characteristics?.facing,
    format: (v, t) => optionLabel('facing', v, t),
  },
  {
    key: 'parkingType',
    icon: stackIcon,
    resolve: (p) => p.characteristics?.parking_type,
    format: (v, t) => optionLabel('parkingType', v, t),
  },
  {
    key: 'acceptsPets',
    icon: stackIcon,
    resolve: (p) => (p.characteristics?.accepts_pets === true ? 'yes' : undefined),
    format: (_v, t) => t('form.drawer.acceptsPets'),
  },
  {
    key: 'balcony',
    icon: stackIcon,
    resolve: (p) => (p.characteristics?.balcony === true ? 'yes' : undefined),
    format: (_v, t) => t('detailView.overview.stats.balcony'),
  },
  {
    key: 'gourmetBalcony',
    icon: stackIcon,
    resolve: (p) => (p.characteristics?.gourmet_balcony === true ? 'yes' : undefined),
    format: (_v, t) => t('detailView.overview.stats.gourmetBalcony'),
  },
];

/**
 * Collects displayable stats from the property characteristics.
 */
export function collectStats(
  property: PropertyResponseType,
  t: TFunction,
): Array<{ icon: React.ReactNode; label: string; value: string }> {
  return STAT_DESCRIPTORS.flatMap((d) => {
    const value = d.resolve(property);
    if (value === undefined || value === null || value === '' || value === 0) return [];
    return [{ icon: d.icon, label: t(`detailView.overview.stats.${d.key}`), value: d.format(value, t) }];
  });
}
